import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectCsvImportById } from "src/store/callrecorder/csvImportsSlice";
import { fetchCsvImport } from "src/actions/callrecorder/csvImportsActions";
import { fetchProviders } from "src/actions/callrecorder/providersActions";

import { DownloadOutlined } from "@ant-design/icons";
import { Button, Tag } from "antd";
import PageLayout from "../_partials/PageLayout";
import SummaryShow from "src/componets/SummaryShow";
import SidebarShow from "./_partials/_SidebarShow";
import Details from "./_partials/_Details";
import RateHistories from "./_partials/_RateHistories";
import PriceHistories from "./_partials/_PriceHistories";
import Icon from "src/componets/Icon";

const POLL_INTERVAL = 3000;

const Show: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const record = useAppSelector(state => id ? selectCsvImportById(state, id) : undefined);
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  // Initial fetch
  useEffect(() => {
    if (id) {
      dispatch(fetchCsvImport(id));
    }
    dispatch(fetchProviders({}));
  }, [id, dispatch]);

  // Polling while processing
  useEffect(() => {
    if (!id) return;

    if (record && (record.status === 'pending' || record.status === 'processing')) {
      intervalRef.current = setInterval(() => {
        dispatch(fetchCsvImport(id));
      }, POLL_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [id, record?.status, dispatch]);

  const STATUS_MAP: Record<string, { color: string; label: string }> = {
    pending:    { color: 'default',    label: 'Pending' },
    processing: { color: 'processing', label: 'Processing' },
    completed:  { color: 'success',    label: 'Completed' },
    failed:     { color: 'error',      label: 'Failed' },
  };

  function StatusTag() {
    if (!record) return null;

    const tag = STATUS_MAP[record.status] || STATUS_MAP.pending;
    return <Tag color={tag.color}>{tag.label}</Tag>;
  }

  return (
    <PageLayout className='v-up-phone-number-prices-show'>
      <div className='v-container-fluid'>
        <div className='o-header'>
          {
            record &&
              <SummaryShow
                title={`CSV Import #${id}`}
                subtitle={<StatusTag />}
                icon={<Icon icon="fa-solid fa-file-csv" />}
                createdAt={record.created_at}
                updatedAt={record.updated_at}
                className='v-mb-15'
                actions={[
                  <Button
                    key='download-csv'
                    className='v-link2'
                    type='link'
                    icon={<DownloadOutlined />}
                    href={record.csv_file_url || undefined}
                    target='_blank'
                    disabled={!record.csv_file_url}
                  >
                    Download
                  </Button>
                ]}
              />
          }
        </div>

        <Details record={record} />

        {record?.import_type === 'rate' && <RateHistories csvImportId={id} processedRows={record?.processed_rows} />}
        {record?.import_type === 'phone_number_price' && <PriceHistories csvImportId={id} processedRows={record?.processed_rows} />}
      </div>

      <SidebarShow record={record} />
    </PageLayout>
  )
}

export default Show;
